import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import bs58 from 'bs58';
import '../styles/admin.css';

const AdminDashboard = () => {
  const { publicKey, signMessage, connected, disconnect: walletDisconnect } = useWallet();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Admin data
  const [stats, setStats] = useState(null);
  const [currentEpoch, setCurrentEpoch] = useState(null);
  const [recentRewards, setRecentRewards] = useState([]);
  const [cronStatus, setCronStatus] = useState(null);
  
  // Featured applications
  const [featuredApps, setFeaturedApps] = useState([]);
  const [featuredCounts, setFeaturedCounts] = useState({ pending: 0, reviewing: 0, approved: 0, rejected: 0 });
  const [featuredFilter, setFeaturedFilter] = useState('pending');
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  const API_URL = import.meta.env.VITE_API_URL || 'https://node.canarymessenger.com';

  // Check if already authenticated
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('admin_auth');
    if (savedAuth) {
      const auth = JSON.parse(savedAuth);
      setIsAuthenticated(true);
      setWalletAddress(auth.wallet);
      fetchAdminData(auth.wallet);
    }
  }, []);

  // Auto-refresh admin data every 30 seconds
  useEffect(() => {
    if (isAuthenticated && walletAddress) {
      const interval = setInterval(() => {
        fetchAdminData(walletAddress);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, walletAddress]);

  // Auto-authenticate when wallet connects
  useEffect(() => {
    if (connected && publicKey && !isAuthenticated) {
      authenticateWallet();
    }
  }, [connected, publicKey]);

  // Fetch featured applications when authenticated or filter changes
  useEffect(() => {
    if (isAuthenticated && walletAddress) {
      fetchFeaturedApps();
    }
  }, [isAuthenticated, walletAddress, featuredFilter]);

  const authenticateWallet = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!publicKey || !signMessage) {
        throw new Error('Wallet not connected');
      }

      const walletAddr = publicKey.toString();
      
      // Create message to sign
      const message = `Sign this message to authenticate as admin of Canary Node Backend.\n\nTimestamp: ${Date.now()}`;
      const encodedMessage = new TextEncoder().encode(message);
      
      // Request signature
      const signature = await signMessage(encodedMessage);
      const signatureBase58 = bs58.encode(signature);
      
      // Verify signature with backend
      const verifyResponse = await fetch(`${API_URL}/api/admin/verify-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet: walletAddr,
          message,
          signature: signatureBase58
        })
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        throw new Error(errorData.error || 'Authentication failed');
      }
      
      // Save authentication
      sessionStorage.setItem('admin_auth', JSON.stringify({
        wallet: walletAddr,
        signature: signatureBase58,
        timestamp: Date.now()
      }));

      setIsAuthenticated(true);
      setWalletAddress(walletAddr);
      fetchAdminData(walletAddr);

    } catch (err) {
      console.error('Wallet authentication error:', err);
      setError(err.message || 'Failed to authenticate wallet');
      walletDisconnect();
    } finally {
      setLoading(false);
    }
  };

  const disconnect = () => {
    sessionStorage.removeItem('admin_auth');
    setIsAuthenticated(false);
    setWalletAddress(null);
    setStats(null);
    setRecentRewards([]);
    setCronStatus(null);
    walletDisconnect();
  };

  const fetchAdminData = async (wallet) => {
    try {
      const auth = sessionStorage.getItem('admin_auth');
      if (!auth) return;

      const { signature } = JSON.parse(auth);

      // Fetch admin stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          'X-Admin-Wallet': wallet,
          'X-Admin-Signature': signature
        }
      });
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
        setCurrentEpoch(statsData.current_epoch);
      }

      // Fetch recent rewards
      const rewardsRes = await fetch(`${API_URL}/api/admin/recent-rewards?limit=20`, {
        headers: {
          'X-Admin-Wallet': wallet,
          'X-Admin-Signature': signature
        }
      });
      
      if (rewardsRes.ok) {
        const rewardsData = await rewardsRes.json();
        setRecentRewards(rewardsData.rewards || []);
      }

      // Fetch cron status
      const cronRes = await fetch(`${API_URL}/api/admin/cron-status`, {
        headers: {
          'X-Admin-Wallet': wallet,
          'X-Admin-Signature': signature
        }
      });
      
      if (cronRes.ok) {
        const cronData = await cronRes.json();
        setCronStatus(cronData);
      }

    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    }
  };

  const triggerEpochDistribution = async () => {
    if (!confirm('Trigger epoch distribution now? This will distribute rewards for the current epoch.')) {
      return;
    }

    try {
      setLoading(true);
      const auth = sessionStorage.getItem('admin_auth');
      const { signature } = JSON.parse(auth);

      const response = await fetch(`${API_URL}/api/admin/trigger-epoch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Wallet': walletAddress,
          'X-Admin-Signature': signature
        }
      });

      if (!response.ok) {
        throw new Error('Failed to trigger epoch distribution');
      }

      const data = await response.json();
      alert(`Epoch distribution triggered: ${data.message}`);
      fetchAdminData(walletAddress);

    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatCRED = (amount) => {
    return Number(amount).toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  // Featured Applications Functions
  const fetchFeaturedApps = async () => {
    try {
      const auth = sessionStorage.getItem('admin_auth');
      if (!auth) return;
      const { signature, wallet } = JSON.parse(auth);

      const res = await fetch(`${API_URL}/api/featured/admin/applications?status=${featuredFilter}`, {
        headers: {
          'X-Admin-Wallet': wallet,
          'X-Admin-Signature': signature
        }
      });

      if (res.ok) {
        const data = await res.json();
        setFeaturedApps(data.applications || []);
        setFeaturedCounts(data.counts || { pending: 0, reviewing: 0, approved: 0, rejected: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch featured apps:', err);
    }
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    const statusMessages = {
      reviewing: 'Move this application to review?',
      approved: 'Approve this application? An email and in-app notification will be sent.',
      rejected: 'Reject this application? An email will be sent to the applicant.'
    };

    if (!confirm(statusMessages[newStatus] || 'Update status?')) return;

    try {
      setLoading(true);
      const auth = sessionStorage.getItem('admin_auth');
      const { signature, wallet } = JSON.parse(auth);

      const res = await fetch(`${API_URL}/api/featured/admin/applications/${applicationId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Wallet': wallet,
          'X-Admin-Signature': signature
        },
        body: JSON.stringify({
          status: newStatus,
          notes: reviewNotes,
          custom_message: customMessage || undefined,
          send_notification: true
        })
      });

      if (!res.ok) throw new Error('Failed to update status');

      alert(`Application ${newStatus}!`);
      setSelectedApp(null);
      setReviewNotes('');
      setCustomMessage('');
      fetchFeaturedApps();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="admin-login">
          <h1>🔐 Admin Dashboard</h1>
          <p className="subtitle">Connect your admin wallet to access controls</p>
          
          {error && <div className="error-box">{error}</div>}
          
          <div className="wallet-button-container">
            <WalletMultiButton />
          </div>
          
          {loading && <p className="loading-text">Authenticating...</p>}
          
          <p className="info-text">
            Only authorized admin wallets can access this dashboard
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div>
          <h1>⚙️ Canary Admin Dashboard</h1>
          <p className="wallet-info">Connected: {formatAddress(walletAddress)}</p>
        </div>
        <button className="disconnect-btn" onClick={disconnect}>
          Disconnect
        </button>
      </header>

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'featured' ? 'active' : ''}`}
          onClick={() => setActiveTab('featured')}
        >
          ⭐ Featured Apps {featuredCounts.pending > 0 && <span className="badge">{featuredCounts.pending}</span>}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => setActiveTab('rewards')}
        >
          🎁 Rewards
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Current Epoch</div>
                <div className="stat-value">{stats.current_epoch}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Rewards</div>
                <div className="stat-value">{formatCRED(stats.total_rewards_distributed)} CRED</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Active Nodes</div>
                <div className="stat-value">{stats.active_nodes || 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Pending Claims</div>
                <div className="stat-value">{stats.pending_claims || 0}</div>
              </div>
            </div>
          )}

          {cronStatus && (
            <div className="cron-section">
              <h2>Cron Job Status</h2>
              <div className="cron-grid">
                <div className="cron-card">
                  <span className="cron-name">Gift Expiration</span>
                  <span className={`status-badge ${cronStatus.gift_expiration ? 'active' : 'inactive'}`}>
                    {cronStatus.gift_expiration ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="cron-card">
                  <span className="cron-name">Buyback Expiration</span>
                  <span className={`status-badge ${cronStatus.buyback_expiration ? 'active' : 'inactive'}`}>
                    {cronStatus.buyback_expiration ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="cron-card">
                  <span className="cron-name">Relay Wallet Monitor</span>
                  <span className={`status-badge ${cronStatus.relay_monitor ? 'active' : 'inactive'}`}>
                    {cronStatus.relay_monitor ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="cron-card">
                  <span className="cron-name">Partner Drops</span>
                  <span className={`status-badge ${cronStatus.partner_drops ? 'active' : 'inactive'}`}>
                    {cronStatus.partner_drops ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="controls-section">
            <h2>Controls</h2>
            <button 
              className="action-btn primary"
              onClick={triggerEpochDistribution}
              disabled={loading}
            >
              🚀 Trigger Epoch Distribution
            </button>
          </div>
        </>
      )}

      {/* Featured Applications Tab */}
      {activeTab === 'featured' && (
        <div className="featured-section">
          <div className="featured-header">
            <h2>⭐ Featured Applications</h2>
            <div className="filter-tabs">
              {['pending', 'reviewing', 'approved', 'rejected'].map((status) => (
                <button
                  key={status}
                  className={`filter-btn ${featuredFilter === status ? 'active' : ''}`}
                  onClick={() => setFeaturedFilter(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)} ({featuredCounts[status] || 0})
                </button>
              ))}
            </div>
          </div>

          {/* Application Detail Modal */}
          {selectedApp && (
            <div className="modal-overlay" onClick={() => setSelectedApp(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{selectedApp.project_name}</h3>
                  <button className="close-btn" onClick={() => setSelectedApp(null)}>×</button>
                </div>
                
                <div className="modal-body">
                  <div className="app-detail-grid">
                    <div className="detail-item">
                      <label>Type</label>
                      <span>{selectedApp.project_type}</span>
                    </div>
                    <div className="detail-item">
                      <label>Budget</label>
                      <span className={`budget-tag ${selectedApp.budget?.includes('10+') ? 'high' : selectedApp.budget?.includes('5 -') ? 'medium' : ''}`}>
                        {selectedApp.budget}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Email</label>
                      <a href={`mailto:${selectedApp.email}`}>{selectedApp.email}</a>
                    </div>
                    <div className="detail-item">
                      <label>Twitter</label>
                      <span>{selectedApp.twitter ? `@${selectedApp.twitter}` : 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Website</label>
                      {selectedApp.website ? (
                        <a href={selectedApp.website} target="_blank" rel="noopener noreferrer">{selectedApp.website}</a>
                      ) : <span>N/A</span>}
                    </div>
                    <div className="detail-item">
                      <label>Wallet</label>
                      <span className="mono">{formatAddress(selectedApp.wallet_address)}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <label>Description</label>
                    <p>{selectedApp.description}</p>
                  </div>

                  {selectedApp.why_feature && (
                    <div className="detail-section">
                      <label>Why Feature</label>
                      <p>{selectedApp.why_feature}</p>
                    </div>
                  )}

                  <div className="detail-section">
                    <label>Internal Notes</label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add internal review notes..."
                      rows={3}
                    />
                  </div>

                  <div className="detail-section">
                    <label>Custom Message (optional - included in email)</label>
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Custom message for the applicant..."
                      rows={2}
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  {selectedApp.status === 'pending' && (
                    <>
                      <button 
                        className="action-btn secondary"
                        onClick={() => updateApplicationStatus(selectedApp.application_id, 'reviewing')}
                        disabled={loading}
                      >
                        👀 Move to Review
                      </button>
                      <button 
                        className="action-btn danger"
                        onClick={() => updateApplicationStatus(selectedApp.application_id, 'rejected')}
                        disabled={loading}
                      >
                        ❌ Reject
                      </button>
                      <button 
                        className="action-btn success"
                        onClick={() => updateApplicationStatus(selectedApp.application_id, 'approved')}
                        disabled={loading}
                      >
                        ✅ Approve
                      </button>
                    </>
                  )}
                  {selectedApp.status === 'reviewing' && (
                    <>
                      <button 
                        className="action-btn danger"
                        onClick={() => updateApplicationStatus(selectedApp.application_id, 'rejected')}
                        disabled={loading}
                      >
                        ❌ Reject
                      </button>
                      <button 
                        className="action-btn success"
                        onClick={() => updateApplicationStatus(selectedApp.application_id, 'approved')}
                        disabled={loading}
                      >
                        ✅ Approve
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Applications List */}
          {featuredApps.length === 0 ? (
            <p className="no-data">No {featuredFilter} applications</p>
          ) : (
            <div className="featured-list">
              {featuredApps.map((app) => (
                <div 
                  key={app.application_id} 
                  className="featured-card"
                  onClick={() => {
                    setSelectedApp(app);
                    setReviewNotes(app.notes || '');
                  }}
                >
                  <div className="card-header">
                    <h4>{app.project_name}</h4>
                    <span className={`budget-tag ${app.budget?.includes('10+') ? 'high' : app.budget?.includes('5 -') ? 'medium' : ''}`}>
                      {app.budget}
                    </span>
                  </div>
                  <div className="card-meta">
                    <span className="type-badge">{app.project_type}</span>
                    <span className="date">{new Date(app.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="card-desc">{app.description?.slice(0, 100)}...</p>
                  <div className="card-footer">
                    <span>{app.email}</span>
                    {app.twitter && <span>@{app.twitter}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rewards Tab */}
      {activeTab === 'rewards' && (
        <div className="rewards-section">
          <h2>Recent Rewards ({recentRewards.length})</h2>
          {recentRewards.length === 0 ? (
            <p className="no-data">No recent rewards</p>
          ) : (
            <div className="rewards-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Epoch</th>
                    <th>Wallet</th>
                    <th>Node ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRewards.map((reward) => (
                    <tr key={reward.id}>
                      <td>{reward.epoch_number}</td>
                      <td className="mono">{formatAddress(reward.wallet_address)}</td>
                      <td className="mono">{formatAddress(reward.node_id)}</td>
                      <td className="amount">{formatCRED(reward.amount)} CRED</td>
                      <td>
                        <span className={`status-badge status-${reward.status}`}>
                          {reward.status}
                        </span>
                      </td>
                      <td>{new Date(reward.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <footer className="admin-footer">
        <a href="/rewards" target="_blank" className="link">View Public Rewards →</a>
      </footer>
    </div>
  );
};

export default AdminDashboard;
